import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { 
  ArrowLeft, MapPin, Users, Car, Shield, Clock, Plane, Star, Phone, Mail,
  CheckCircle, Info, DollarSign, Settings, Fuel, Calendar, Globe, 
  Wifi, Snowflake, Music, Battery, Wine
} from 'lucide-react';
import { Layout } from '../components/layout/Layout';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fadeInUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] } }
};

const stagger = {
  visible: { transition: { staggerChildren: 0.1 } }
};

const amenityIcons = {
  'aire acondicionado': Snowflake,
  'wifi': Wifi,
  'cargadores': Battery,
  'bebidas': Wine,
  'sonido premium': Music,
  'default': CheckCircle
};

export default function TransportDetailPage() {
  const { slug } = useParams();
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const [service, setService] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(0);


  useEffect(() => {
    const fetchService = async () => {
      try {
        const response = await axios.get(`${API}/public/transport/${slug}`);
        setService(response.data);
      } catch (error) {
        console.error('Error fetching transport service:', error);
        setService(null);
      } finally {
        setLoading(false);
      }
    };
    fetchService();
  }, [slug]);

  const getAmenityIcon = (amenity) => {
    const lower = amenity.toLowerCase();
    for (const [key, Icon] of Object.entries(amenityIcons)) {
      if (lower.includes(key)) return Icon;
    }
    return CheckCircle;
  };

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="w-12 h-12 border-4 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
        </div>
      </Layout>
    );
  }

  if (!service) {
    return (
      <Layout>
        <div className="min-h-screen flex flex-col items-center justify-center">
          <Car size={64} className="text-white/20 mb-4" />
          <h1 className="text-2xl text-white mb-4">Servicio no encontrado</h1>
          <Link to="/transport" className="text-[#D4AF37] hover:underline">
            Volver a Transportación
          </Link>
        </div>
      </Layout>
    );
  }

  const images = service.gallery?.length > 0 ? service.gallery : [service.cover_image];

  return (
    <Layout>
      {/* Back Button */}
      <div className="container-premium pt-24 pb-4">
        <Link 
          to="/transport" 
          className="inline-flex items-center gap-2 text-white/60 hover:text-white transition-colors"
          data-testid="back-btn"
        >
          <ArrowLeft size={20} />
          Volver a Transportación
        </Link>
      </div>

      {/* Hero Section */}
      <section className="section-dark pb-12" data-testid="transport-detail-hero">
        <div className="container-premium">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={stagger}
            className="grid grid-cols-1 lg:grid-cols-2 gap-8"
          >
            {/* Gallery */}
            <motion.div variants={fadeInUp} className="space-y-4">
              <div className="aspect-[4/3] rounded-xl overflow-hidden">
                <img
                  src={images[selectedImage]}
                  alt={service.business_name?.[lang] || service.business_name?.es}
                  className="w-full h-full object-cover"
                />
              </div>
              {images.length > 1 && (
                <div className="grid grid-cols-4 gap-2">
                  {images.map((img, index) => (
                    <button
                      key={index}
                      onClick={() => setSelectedImage(index)}
                      className={`aspect-[4/3] rounded-lg overflow-hidden border-2 transition-colors ${
                        selectedImage === index ? 'border-[#D4AF37]' : 'border-transparent'
                      }`}
                    >
                      <img src={img} alt="" className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </motion.div>

            {/* Info */}
            <motion.div variants={fadeInUp} className="space-y-6">
              {service.featured && (
                <div className="inline-flex items-center gap-1 px-3 py-1 bg-[#D4AF37] text-black text-sm font-medium rounded-full">
                  <Star size={14} /> Destacado
                </div>
              )}
              
              <div>
                <div className="flex flex-wrap gap-2 mb-3">
                  {service.vehicle_types?.map((type) => (
                    <span key={type} className="text-[#D4AF37] text-sm tracking-wider uppercase">
                      {type}
                    </span>
                  ))}
                </div>
                <h1 className="font-display text-4xl md:text-5xl text-white mb-2">
                  {service.business_name?.[lang] || service.business_name?.es}
                </h1>
                {service.provider_name && (
                  <p className="text-white/60">{service.provider_name}</p>
                )}
              </div>

              <div className="flex flex-wrap gap-4 text-white/70">
                <span className="flex items-center gap-2">
                  <MapPin size={18} className="text-[#D4AF37]" />
                  {service.city}, {service.country}
                </span>
                <span className="flex items-center gap-2">
                  <Users size={18} className="text-[#D4AF37]" />
                  {service.passenger_capacity} pasajeros
                </span>
                {service.units_available > 1 && (
                  <span className="flex items-center gap-2">
                    <Car size={18} className="text-[#D4AF37]" />
                    {service.units_available} unidades
                  </span>
                )}
              </div>

              {/* Service Levels */}
              <div className="flex flex-wrap gap-2">
                {service.service_levels?.map((level) => (
                  <span 
                    key={level} 
                    className="px-3 py-1 bg-white/5 border border-white/10 rounded-full text-white/80 text-sm capitalize"
                  >
                    {level}
                  </span>
                ))}
              </div>

              {/* Quick Info */}
              <div className="grid grid-cols-2 gap-4">
                {service.airport_service && (
                  <div className="flex items-center gap-2 text-white/70">
                    <Plane size={18} className="text-[#C0C0C0]" />
                    Servicio aeropuerto
                  </div>
                )}
                {service.night_availability && (
                  <div className="flex items-center gap-2 text-white/70">
                    <Clock size={18} className="text-[#C0C0C0]" />
                    Disponible 24/7
                  </div>
                )}
                {service.out_of_town && (
                  <div className="flex items-center gap-2 text-white/70">
                    <Globe size={18} className="text-[#C0C0C0]" />
                    Servicio foráneo
                  </div>
                )}
                {service.with_driver && (
                  <div className="flex items-center gap-2 text-white/70">
                    <Shield size={18} className="text-[#C0C0C0]" />
                    Con chofer
                  </div>
                )}
              </div>

              {/* Pricing */}
              <div className="glass-dark rounded-xl p-6 border border-white/10">
                <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                  <DollarSign size={20} className="text-[#D4AF37]" />
                  Tarifas
                </h3>
                {service.pricing?.quote_only ? (
                  <p className="text-[#D4AF37] text-2xl font-display">Solicitar cotización</p>
                ) : (
                  <div className="grid grid-cols-2 gap-4">
                    {service.pricing?.price_per_hour && (
                      <div>
                        <p className="text-white/50 text-sm">Por hora</p>
                        <p className="text-white text-lg">${service.pricing.price_per_hour.toLocaleString()} {service.currency}</p>
                      </div>
                    )}
                    {service.pricing?.price_per_day && (
                      <div>
                        <p className="text-white/50 text-sm">Por día</p>
                        <p className="text-white text-lg">${service.pricing.price_per_day.toLocaleString()} {service.currency}</p>
                      </div>
                    )}
                    {service.pricing?.price_per_transfer && (
                      <div>
                        <p className="text-white/50 text-sm">Por traslado</p>
                        <p className="text-white text-lg">${service.pricing.price_per_transfer.toLocaleString()} {service.currency}</p>
                      </div>
                    )}
                    {service.pricing?.extra_time_cost && (
                      <div>
                        <p className="text-white/50 text-sm">Tiempo extra</p>
                        <p className="text-white text-lg">${service.pricing.extra_time_cost.toLocaleString()} {service.currency}/hr</p>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Contact Buttons */}
              <div className="flex flex-col sm:flex-row gap-3">
                {service.whatsapp && (
                  <a
                    href={`https://wa.me/${service.whatsapp.replace(/[^0-9]/g, '')}?text=Hola, me interesa el servicio de transporte`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-[#25D366] text-white font-semibold rounded-lg hover:bg-[#20BD5A] transition-colors"
                    data-testid="whatsapp-btn"
                  >
                    <Phone size={20} />
                    WhatsApp
                  </a>
                )}
                {service.email && (
                  <a
                    href={`mailto:${service.email}?subject=Solicitud de cotización&body=Hola, me interesa solicitar una cotización para servicio de transporte.`}
                    className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-[#D4AF37] text-black font-semibold rounded-lg hover:bg-[#B8962E] transition-colors"
                    data-testid="email-btn"
                  >
                    <Mail size={20} />
                    Enviar correo
                  </a>
                )}
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Details Section */}
      <section className="section-subtle py-16">
        <div className="container-premium">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={stagger}
            className="grid grid-cols-1 lg:grid-cols-3 gap-8"
          >
            {/* Description */}
            <motion.div variants={fadeInUp} className="lg:col-span-2 space-y-8">
              <div>
                <h2 className="text-white text-2xl font-display mb-4">Descripción del servicio</h2>
                <p className="text-white/70 leading-relaxed">
                  {service.description?.[lang] || service.description?.es}
                </p>
              </div>

              {/* Vehicle Info */}
              <div className="glass-dark rounded-xl p-6 border border-white/10">
                <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                  <Car size={20} className="text-[#D4AF37]" />
                  Información del vehículo
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {service.brand && (
                    <div>
                      <p className="text-white/50 text-sm">Marca</p>
                      <p className="text-white">{service.brand}</p>
                    </div>
                  )}
                  {service.model_name && (
                    <div>
                      <p className="text-white/50 text-sm">Modelo</p>
                      <p className="text-white">{service.model_name}</p>
                    </div>
                  )}
                  {service.year && (
                    <div>
                      <p className="text-white/50 text-sm">Año</p>
                      <p className="text-white">{service.year}</p>
                    </div>
                  )}
                  {service.color && (
                    <div>
                      <p className="text-white/50 text-sm">Color</p>
                      <p className="text-white">{service.color}</p>
                    </div>
                  )}
                  {service.luggage_capacity && (
                    <div>
                      <p className="text-white/50 text-sm">Equipaje</p>
                      <p className="text-white">{service.luggage_capacity}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Amenities */}
              {service.amenities?.length > 0 && (
                <div>
                  <h3 className="text-white font-semibold mb-4">Amenidades incluidas</h3>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {service.amenities.map((amenity, index) => {
                      const Icon = getAmenityIcon(amenity);
                      return (
                        <div key={index} className="flex items-center gap-2 text-white/70">
                          <Icon size={16} className="text-[#D4AF37]" />
                          <span className="capitalize">{amenity}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Service Modes */}
              <div>
                <h3 className="text-white font-semibold mb-4">Modalidades de servicio</h3>
                <div className="flex flex-wrap gap-2">
                  {service.by_hour && (
                    <span className="px-3 py-1 bg-white/5 border border-white/10 rounded-full text-white/80 text-sm">
                      Por hora
                    </span>
                  )}
                  {service.by_day && (
                    <span className="px-3 py-1 bg-white/5 border border-white/10 rounded-full text-white/80 text-sm">
                      Por día
                    </span>
                  )}
                  {service.by_transfer && (
                    <span className="px-3 py-1 bg-white/5 border border-white/10 rounded-full text-white/80 text-sm">
                      Por traslado
                    </span>
                  )}
                  {service.by_event && (
                    <span className="px-3 py-1 bg-white/5 border border-white/10 rounded-full text-white/80 text-sm">
                      Por evento
                    </span>
                  )}
                </div>
              </div>
            </motion.div>

            {/* Sidebar */}
            <motion.div variants={fadeInUp} className="space-y-6">
              {/* Coverage */}
              {service.coverage_areas?.length > 0 && (
                <div className="glass-dark rounded-xl p-6 border border-white/10">
                  <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                    <MapPin size={20} className="text-[#D4AF37]" />
                    Cobertura
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {service.coverage_areas.map((area, index) => (
                      <span key={index} className="px-3 py-1 bg-white/5 rounded-full text-white/70 text-sm">
                        {area}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Requirements */}
              {service.requirements && Object.keys(service.requirements).length > 0 && (
                <div className="glass-dark rounded-xl p-6 border border-white/10">
                  <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                    <Info size={20} className="text-[#D4AF37]" />
                    Requisitos
                  </h3>
                  <ul className="space-y-2">
                    {service.requirements.id_required && (
                      <li className="flex items-center gap-2 text-white/70 text-sm">
                        <CheckCircle size={14} className="text-[#D4AF37]" />
                        Identificación oficial
                      </li>
                    )}
                    {service.requirements.advance_payment && (
                      <li className="flex items-center gap-2 text-white/70 text-sm">
                        <CheckCircle size={14} className="text-[#D4AF37]" />
                        Pago anticipado
                      </li>
                    )}
                    {service.requirements.contract_required && (
                      <li className="flex items-center gap-2 text-white/70 text-sm">
                        <CheckCircle size={14} className="text-[#D4AF37]" />
                        Firma de contrato
                      </li>
                    )}
                    {service.requirements.cancellation_policy && (
                      <li className="text-white/50 text-xs mt-3 pt-3 border-t border-white/10">
                        {service.requirements.cancellation_policy}
                      </li>
                    )}
                  </ul>
                </div>
              )}

              {/* Contact Info */}
              <div className="glass-dark rounded-xl p-6 border border-white/10">
                <h3 className="text-white font-semibold mb-4">Contacto</h3>
                <div className="space-y-3">
                  {service.phone && (
                    <a href={`tel:${service.phone}`} className="flex items-center gap-2 text-white/70 hover:text-white transition-colors">
                      <Phone size={16} className="text-[#D4AF37]" />
                      {service.phone}
                    </a>
                  )}
                  {service.email && (
                    <a href={`mailto:${service.email}`} className="flex items-center gap-2 text-white/70 hover:text-white transition-colors">
                      <Mail size={16} className="text-[#D4AF37]" />
                      {service.email}
                    </a>
                  )}
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </Layout>
  );
}
